import React from "react";
import { useNavigate } from "react-router-dom";
import { LockSimple } from "@phosphor-icons/react";
import {
  Microphone, User as UserIcon, Target,
  Newspaper, EnvelopeSimple, DownloadSimple,
  MonitorPlay, Layout, ChatsCircle,
  // Subcard ikonok
  MagnifyingGlass, PencilSimple,
  UsersThree, Briefcase, FilmStrip, StackSimple,
  HandWaving, BookOpen, Hand,
  Lightning,
  Wrench, Phone,
  Gift,
  ChatCircle, DeviceMobile, Sparkle,
} from "@phosphor-icons/react";
import AgentCard from "../components/AgentCard";
import { useAuth } from "../contexts/AuthContext";
import { useAgentVisibility } from "../lib/agentVisibility";
import { LAYER_LABELS } from "../lib/agentRegistry";
import { toast } from "sonner";

/* ════════════════════════════════════════════════════════════════════
 * AGENT_BLOCKS — a 9 fő ügynök + a hozzájuk tartozó subcardok regisztere.
 * Csak az appban már létező ügynököket és sub-választókat tartalmazza.
 * A `frontendKey` belső kulcsok (voice / client / offer / magnet-post /
 * email / lead-magnet / webinar / landing / sales) — NE nevezd át.
 * ══════════════════════════════════════════════════════════════════ */
const AGENT_BLOCKS = [
  // ─── Alapozás ─────────────────────────────────────────────────
  {
    frontendKey: "voice", layer: "foundation",
    mainIcon: Microphone,
    title: "Márkahang Varázsló",
    description: "A hang, amin minden anyagod megszólal.",
    route: "/agents/voice",
    subCards: [
      { title: "Márkahang Expressz", desc: "Elemzés és stílusprofil meglévő anyagból.", icon: MagnifyingGlass },
      { title: "Márkahang Építő",    desc: "Karakteres hangolás irányított kérdésekkel.", icon: PencilSimple },
    ],
  },
  {
    frontendKey: "client", layer: "foundation",
    mainIcon: UserIcon,
    title: "Avatar Varázsló",
    description: "Az ügyfél, akinek minden üzeneted szól.",
    route: "/agents/client",
    subCards: [
      { title: "Avatar Expressz", desc: "Tűpontos ügyfélprofil meglévő szövegekből.", icon: MagnifyingGlass },
      { title: "Avatar Építő",    desc: "Karakterépítés célzott kérdések alapján.", icon: PencilSimple },
    ],
  },
  {
    frontendKey: "offer", layer: "foundation",
    mainIcon: Target,
    title: "Ajánlat Varázsló",
    description: "Erősebb ajánlat, könnyebb igen.",
    route: "/agents/offer",
    subCards: [
      { title: "Ajánlat Expressz", desc: "Meglévő ajánlatod prémium szintű javítása.", icon: MagnifyingGlass },
      { title: "Ajánlat Építő",    desc: "Új, erős ajánlatstruktúra felépítése kérdésekből.", icon: PencilSimple },
    ],
  },

  // ─── Vonzás ───────────────────────────────────────────────────
  {
    frontendKey: "magnet-post", layer: "attraction",
    mainIcon: Newspaper,
    title: "Poszt Varázsló",
    description: "Görgetésállító posztok, valódi beszélgetésért.",
    route: "/agents/magnet-post",
    subCards: [
      { title: "Facebook", desc: "Szöveges poszt.", icon: UsersThree },
      { title: "LinkedIn", desc: "Szöveges poszt.", icon: Briefcase },
      { title: "IG Reels", desc: "Rövid videó ötlet és script.", icon: FilmStrip },
      { title: "Carousel", desc: "Képsorozat szövegekkel.", icon: StackSimple },
    ],
  },
  {
    frontendKey: "email", layer: "attraction",
    mainIcon: EnvelopeSimple,
    title: "Email Varázsló",
    description: "Emailek, amelyek közelebb hozzák a döntést.",
    route: "/agents/email",
    subCards: [
      { title: "Üdvözlő sorozat",    desc: "Az első levelek a hosszú távú kapcsolatért.",          icon: HandWaving },
      { title: "Értékadó email",     desc: "Levél, ami tanít és a következő lépés felé lendít.",  icon: BookOpen },
      { title: "Kézfelemelő email",  desc: "Közvetlen levél, amelyből azonnali válasz indul.",    icon: Hand },
      { title: "Webinár promó",      desc: "Meghívók és emlékeztetők a megjelenésért.",           icon: MonitorPlay },
    ],
  },
  {
    frontendKey: "lead-magnet", layer: "attraction",
    mainIcon: DownloadSimple,
    title: "Ügyfélmágnes Varázsló",
    description: "Mini anyag egy gyors győzelemért.",
    route: "/agents/lead-magnet",
    subCards: [
      { title: "Mikromágnes™", desc: "Értékes mini anyag egy gyors győzelemért.", icon: Lightning },
    ],
  },

  // ─── Konverzió ────────────────────────────────────────────────
  {
    frontendKey: "webinar", layer: "conversion",
    mainIcon: MonitorPlay,
    title: "Webinár Varázsló",
    description: "Szerezz ügyfelet online előadással.",
    route: "/agents/webinar",
    subCards: [
      { title: "Webinár javítás",       desc: "A meglévő prezentációd átvilágítása.",         icon: Wrench },
      { title: "Hívásgeneráló előadás", desc: "Előadás, ami konzultációra vezeti a nézőt.",    icon: Phone },
      { title: "Közvetlen értékesítés", desc: "Struktúra azonnali vásárlási döntésre.",        icon: Target },
    ],
  },
  {
    frontendKey: "landing", layer: "conversion",
    mainIcon: Layout,
    title: "Landing Varázsló",
    description: "Hatékony ügyfélszerző oldalak.",
    route: "/agents/landing",
    subCards: [
      { title: "Letöltő oldal",       desc: "Letisztult felület, ahol elkérik az anyagaidat.", icon: Gift },
      { title: "Regisztrációs oldal", desc: "Oldal, ami elkötelezi a nézőt a megjelenésre.",   icon: MonitorPlay },
      { title: "Sales oldal",         desc: "Értékesítési oldal a döntésig.",                    icon: Target },
    ],
  },
  {
    frontendKey: "sales", layer: "conversion",
    mainIcon: ChatsCircle,
    title: "Sales Mesterműhely",
    description: "Értékesítő beszélgetések, nyomulás nélkül.",
    route: "/agents/sales",
    subCards: [
      { title: "10 perces telefon",  desc: "Kiderül, kivel van valódi lehetőség az együttműködésre – és kivel nincs.", icon: DeviceMobile },
      { title: "Sales beszélgetés",  desc: "Magasan konvertáló értékesítési script.",         icon: UsersThree },
      { title: "Hívás nélkül",       desc: "Tisztán üzenet-alapú ügyfélszerzés.",             icon: Sparkle, locked: true },
    ],
  },
];

/* ════════════════════════════════════════════════════════════════════
 * SUB_ACCENT — az al-kártyák tokenei rétegenként. Az alábbi paletta
 * 1:1 másolja az AgentOpeningScreen `accent` objektumát (idle háttér +
 * border + ikon szín) — így az al-kártyák PONTOSAN ugyanúgy néznek ki,
 * ahogy az eredeti helyükön (Poszt / Email / Sales agent input-képernyő).
 * ══════════════════════════════════════════════════════════════════ */
const SUB_ACCENT = {
  foundation: {
    idleBg: "rgba(94,80,247,0.06)",
    idleBorder: "rgba(94,80,247,0.20)",
    hoverBg: "rgba(94,80,247,0.10)",
    hoverBorder: "rgba(94,80,247,0.50)",
    iconBg: "rgba(94,80,247,0.16)",
    iconColor: "#a294ff",
    labelColor: "#9898ad",
  },
  attraction: {
    idleBg: "rgba(117,223,245,0.06)",
    idleBorder: "rgba(117,223,245,0.20)",
    hoverBg: "rgba(117,223,245,0.10)",
    hoverBorder: "rgba(117,223,245,0.50)",
    iconBg: "rgba(117,223,245,0.16)",
    iconColor: "#75dff5",
    labelColor: "#9898ad",
  },
  conversion: {
    idleBg: "rgba(255,219,92,0.06)",
    idleBorder: "rgba(255,219,92,0.20)",
    hoverBg: "rgba(255,219,92,0.10)",
    hoverBorder: "rgba(255,219,92,0.50)",
    iconBg: "rgba(255,219,92,0.16)",
    iconColor: "#ffdb5c",
    labelColor: "#9898ad",
  },
};

/* ════════════════════════════════════════════════════════════════════
 * Layer connector strings (a header eyebrow mellé jelenik meg).
 * ══════════════════════════════════════════════════════════════════ */
const LAYER_TAGS = {
  foundation: "a digitális DNS-ed",
  attraction: "itt születnek az első érdeklődők",
  conversion: "itt lesznek az érdeklődőkből ügyfelek",
};

const LAYER_HEADER_COLOR = {
  foundation: "#9b8cff",
  attraction: "#5fd6ec",
  conversion: "#ffdb5c",
};

/* ════════════════════════════════════════════════════════════════════
 * SubTile — az AgentOpeningScreen `OptionTile` vizuális ikerpárja.
 *   - 220 px széles, aspectRatio 1.15/1 → kissé fekvő négyzet
 *   - középre zárt: ikon felül, cím középen, leírás középen
 *   - réteg-tinted háttér + halvány keret
 *   - locked → HAMAROSAN pill (LockSimple)
 * ══════════════════════════════════════════════════════════════════ */
function SubTile({ sub, layer, parentLocked, parentRoute }) {
  const navigate = useNavigate();
  const accent = SUB_ACCENT[layer];
  const locked = parentLocked || sub.locked;
  const Icon = sub.icon;

  const onClick = () => {
    if (locked) {
      toast.info("Ez az ügynök hamarosan elérhető lesz.");
      return;
    }
    navigate(parentRoute);
  };

  return (
    <button
      type="button"
      data-testid={`agents-sub-${sub.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
      onClick={onClick}
      disabled={locked}
      className="group relative text-center rounded-xl flex flex-col items-center justify-center overflow-hidden"
      style={{
        width: 220,
        aspectRatio: "1.15 / 1",
        background: accent.idleBg,
        border: `1px solid ${accent.idleBorder}`,
        cursor: locked ? "not-allowed" : "pointer",
        padding: "22px 16px",
        opacity: locked ? 0.55 : 1,
        transition: "background 0.25s ease, border-color 0.25s ease, transform 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (!locked) {
          e.currentTarget.style.background = accent.hoverBg;
          e.currentTarget.style.borderColor = accent.hoverBorder;
          e.currentTarget.style.transform = "translateY(-2px)";
        }
      }}
      onMouseLeave={(e) => {
        if (!locked) {
          e.currentTarget.style.background = accent.idleBg;
          e.currentTarget.style.borderColor = accent.idleBorder;
          e.currentTarget.style.transform = "translateY(0)";
        }
      }}
    >
      {locked && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border border-white/10 text-[#9898ad]"
          style={{ background: "rgba(0,0,0,0.25)" }}
        >
          <LockSimple size={10} weight="bold" /> HAMAROSAN
        </span>
      )}

      <div
        className="mb-3 flex items-center justify-center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 12,
          background: accent.iconBg,
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Icon size={22} weight="regular" color={accent.iconColor} />
      </div>

      <div
        className="font-heading mb-1"
        style={{
          fontSize: 17,
          fontWeight: 500,
          letterSpacing: "-0.01em",
          color: "#e8e8f0",
          lineHeight: 1.25,
        }}
      >
        {sub.title}
      </div>
      <div style={{ fontSize: 13, color: accent.labelColor, lineHeight: 1.4 }}>
        {sub.desc}
      </div>
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * AgentRow — 2 oszlop CSS Grid:
 *   - BAL: a Műhely téglalapos `AgentCard`-ja (h-[220px]), 360px wide
 *   - JOBB: subcardok 220px wide négyzetes rács, flex-wrap
 * A jobb oszlopot wrap-eljük, ha sok sub van — DE soha NEM kerül a sub
 * az anya alá (a teljes szélességű sorra), mert az csak a JOBB oszlopon
 * belül tördel.
 * ══════════════════════════════════════════════════════════════════ */
function AgentRow({ block, isAdmin, flagStatus, isLast }) {
  const accent = LAYER_HEADER_COLOR[block.layer];
  if (flagStatus === "hidden" && !isAdmin) return null;
  const teaseAll = flagStatus === "admin_only" && !isAdmin;

  return (
    <div
      data-testid={`agents-row-${block.frontendKey}`}
      className="grid items-start"
      style={{
        gridTemplateColumns: "360px 1fr",
        gap: 18,
        paddingBottom: 22,
        marginBottom: 22,
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.10)",
      }}
    >
      {/* BAL — anya kártya pontosan a Műhely AgentCard-ja */}
      <div style={{ width: 360 }}>
        <AgentCard
          layer={block.layer}
          title={block.title}
          description={block.description}
          icon={block.mainIcon}
          to={block.route}
          status={teaseAll ? "admin-only-tease" : "ready"}
          testid={`agents-main-${block.frontendKey}`}
          onLockedClick={teaseAll ? () => toast.info("Ez az ügynök hamarosan elérhető lesz.") : undefined}
        />
      </div>

      {/* JOBB — subcard sor a jobb oszlopon belül; flex-wrap, de SOHA
          nem ugrik a bal oszlop alá (CSS Grid külön cellában). */}
      <div className="flex flex-wrap" style={{ gap: 14, minWidth: 0 }}>
        {block.subCards.map((sub, idx) => (
          <SubTile
            key={`${block.frontendKey}-sub-${idx}`}
            sub={sub}
            layer={block.layer}
            parentLocked={teaseAll}
            parentRoute={block.route}
          />
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * LayerHeader — Alapozás / Vonzás / Konverzió szekciócím.
 * ══════════════════════════════════════════════════════════════════ */
function LayerHeader({ layer }) {
  const accent = LAYER_HEADER_COLOR[layer];
  return (
    <div
      className="flex items-center gap-3 mb-5 mt-2"
      data-testid={`agents-layer-header-${layer}`}
    >
      <span
        className="block"
        style={{ width: 22, height: 2, background: accent, borderRadius: 2 }}
      />
      <h2
        className="font-heading text-[22px] font-semibold tracking-tight"
        style={{ color: accent }}
      >
        {LAYER_LABELS[layer]}
      </h2>
      <span className="text-[11.5px] tracking-[0.05em]" style={{ color: "#9591a8" }}>
        {LAYER_TAGS[layer]}
      </span>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * Agents — /agents route fő komponens.
 * ══════════════════════════════════════════════════════════════════ */
export default function Agents() {
  const { effectiveRole } = useAuth();
  const { lookup: agentStatusFor } = useAgentVisibility();
  const isAdmin = effectiveRole === "admin";

  const groups = [
    { layer: "foundation", blocks: AGENT_BLOCKS.filter((b) => b.layer === "foundation") },
    { layer: "attraction", blocks: AGENT_BLOCKS.filter((b) => b.layer === "attraction") },
    { layer: "conversion", blocks: AGENT_BLOCKS.filter((b) => b.layer === "conversion") },
  ];

  return (
    <div
      data-testid="agents-page"
      className="px-6 sm:px-10 lg:px-14 pt-10 pb-24"
      style={{ background: "#0A0A0F", minHeight: "100vh", color: "#F2F1F7" }}
    >
      <div className="max-w-[1280px] mx-auto">
        {/* Oldal cím + alcím */}
        <header className="mb-10">
          <h1 className="font-heading text-[34px] sm:text-[40px] font-semibold tracking-tight mb-2">
            Ügynökök
          </h1>
          <p className="text-[14px]" style={{ color: "#9591a8" }}>
            Minden ügynök és a hozzá tartozó formátumok, rétegek szerint rendezve.
          </p>
        </header>

        {/* 3 réteg */}
        <div className="flex flex-col gap-12">
          {groups.map(({ layer, blocks }) => (
            <section key={layer} data-testid={`agents-section-${layer}`}>
              <LayerHeader layer={layer} />
              <div className="flex flex-col">
                {blocks.map((block, idx) => (
                  <AgentRow
                    key={block.frontendKey}
                    block={block}
                    isAdmin={isAdmin}
                    flagStatus={agentStatusFor(block.frontendKey)}
                    isLast={idx === blocks.length - 1}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
